import type { FieldErrors } from 'react-hook-form';

import {
  PatientFormSchemaType,
  PatientFormData,
  defaultPatientFormData,
  defaultPatientData,
  type PatientInformation,
  type HealthInsuranceCard,
  type MedicalAid,
} from '@pages/main/patient/types';
import {
  normalizeAddress,
  normalizeInsurerNumber as normalizeInsurerNumberField,
  normalizePostalCode,
  normalizeHalfWidthCodeField,
} from '@/utils/japaneseText';
import { dateValueToIso } from '@/utils/dateConverter';

/** claim_types.id for 「無し」. 生活保護 normalization fills this from the canonical insurer. */
const CLAIM_TYPE_NONE_ID = 1;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function fieldErrorsTreeEmpty(errors: FieldErrors | undefined): boolean {
  if (!errors || typeof errors !== 'object') return true;
  for (const v of Object.values(errors)) {
    if (v && typeof v === 'object' && v !== null && 'message' in v) return false;
    if (v && typeof v === 'object' && v !== null && !fieldErrorsTreeEmpty(v as FieldErrors)) {
      return false;
    }
  }
  return true;
}

export const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const normalizeDateField = (value: unknown): string => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return '';
  const normalized = dateValueToIso(raw);
  if (normalized) return normalized;
  return ISO_DATE_RE.test(raw) ? raw : '';
};

export const normalizeMedicalAidForSubmit = (
  medicalAid: PatientFormSchemaType['medical_aid'] | undefined
): PatientFormSchemaType['medical_aid'] => {
  const normalizedInsurerTypeId =
    medicalAid?.insurer_type_id === 0 ? 1 : (medicalAid?.insurer_type_id ?? 0);

  if (medicalAid?.insurer_type_id === 0) {
    return {
      ...medicalAid,
      insurer_type_id: normalizedInsurerTypeId,
      calc_type: null,
      limit_amount: null,
      limit_num_month: null,
      limit_amount_month: null,
    };
  }

  return {
    ...medicalAid,
    insurer_type_id: normalizedInsurerTypeId,
  };
};

export const buildPatientFormPayloadForSubmit = (
  data: PatientFormSchemaType
): PatientFormSchemaType => {
  const typeId = data.health_insurance_card?.insurance_type_id;
  const defaultInsurerKindName = typeId === 2 ? '後期高齢' : typeId === 3 ? '生活保護' : '一般';

  return {
    ...data,
    patient_information: {
      ...data.patient_information,
      available_from: data.patient_information?.available_from || undefined,
    },
    health_insurance_card: {
      ...data.health_insurance_card,
      insurer_kind_name:
        (data.health_insurance_card?.insurer_kind_name?.trim() as string) ||
        defaultInsurerKindName,
    },
    medical_aid: normalizeMedicalAidForSubmit(data.medical_aid),
  };
};

/**
 * Normalize patient data to ensure only one record per type is marked as current.
 */
export const normalizePatientData = (data: PatientFormData): PatientFormData => {
  const normalized = { ...data };

  if (normalized.patient_informations) {
    const currentPi = normalized.current_patient_information;
    const newPiDraft = currentPi && currentPi.id == null;

    if (newPiDraft) {
      normalized.patient_informations = normalized.patient_informations.map(info => ({
        ...info,
        is_current: false,
      }));
    } else {
      const currentPatientInfoId = currentPi?.id;
      let hasCurrent = false;

      normalized.patient_informations = normalized.patient_informations.map(info => {
        if (currentPatientInfoId && info.id === currentPatientInfoId) {
          hasCurrent = true;
          return { ...info, is_current: true };
        } else if (info.is_current && !hasCurrent && !currentPatientInfoId) {
          hasCurrent = true;
          return info;
        }
        return { ...info, is_current: false };
      });

      if (!hasCurrent && currentPatientInfoId) {
        const currentInfo = normalized.patient_informations.find(
          info => info.id === currentPatientInfoId
        );
        if (currentInfo) {
          currentInfo.is_current = true;
        }
      }

      if (!hasCurrent && normalized.patient_informations.length > 0) {
        normalized.patient_informations[0].is_current = true;
      }

      const currentInfo = normalized.patient_informations.find(info => info.is_current);
      if (currentInfo) {
        normalized.patient_informations = [
          currentInfo,
          ...normalized.patient_informations.filter(info => info.id !== currentInfo.id),
        ];
      }
    }
  }

  if (normalized.health_insurance_cards) {
    let hasCurrent = false;
    normalized.health_insurance_cards = normalized.health_insurance_cards.map(card => {
      if (card.is_current && !hasCurrent) {
        hasCurrent = true;
        return card;
      }
      return { ...card, is_current: false };
    });
    if (!hasCurrent && normalized.health_insurance_cards.length > 0) {
      normalized.health_insurance_cards[0].is_current = true;
    }
    const currentCard = normalized.health_insurance_cards.find(card => card.is_current);
    if (currentCard) {
      normalized.health_insurance_cards = [
        currentCard,
        ...normalized.health_insurance_cards.filter(card => card.id !== currentCard.id),
      ];
    }
  }

  if (normalized.medical_aids) {
    let hasCurrent = false;
    normalized.medical_aids = normalized.medical_aids.map(aid => {
      if (aid.is_current && !hasCurrent) {
        hasCurrent = true;
        return aid;
      }
      return { ...aid, is_current: false };
    });
    if (!hasCurrent && normalized.medical_aids.length > 0) {
      normalized.medical_aids[0].is_current = true;
    }
    const currentAid = normalized.medical_aids.find(aid => aid.is_current);
    if (currentAid) {
      normalized.medical_aids = [
        currentAid,
        ...normalized.medical_aids.filter(aid => aid.id !== currentAid.id),
      ];
    }
  }

  return normalized;
};

/**
 * BD 6.1 — merge certification navigation data onto the DB patient while preserving DB address fields.
 */
export const mergeCertificationCopiedPatient = (
  normalizedData: PatientFormData,
  certData: PatientFormData
): PatientFormData => {
  let mergedData = normalizedData;
  const certPi = certData.current_patient_information ?? certData.patient_information;
  const certHic =
    certData.health_insurance_cards?.find(c => c.is_current) ?? certData.health_insurance_card;
  const dbPi = mergedData.current_patient_information ?? mergedData.patient_information;
  const dbHic =
    mergedData.health_insurance_cards?.find(c => c.is_current) ?? mergedData.health_insurance_card;

  if (certPi && dbPi) {
    const mergedPi: typeof dbPi = {
      ...dbPi,
      name: certPi.name ?? dbPi.name,
      name_kana: certPi.name_kana ?? dbPi.name_kana,
      sex_id: certPi.sex_id ?? dbPi.sex_id,
      date_of_birth: certPi.date_of_birth ?? dbPi.date_of_birth,
    };
    mergedData = {
      ...mergedData,
      current_patient_information: mergedPi,
      patient_information: mergedPi,
      patient_informations: mergedData.patient_informations?.map(p =>
        p.is_current ? mergedPi : p
      ),
    };
  }

  if (certHic && dbHic) {
    const mergedHic: typeof dbHic = {
      ...dbHic,
      insurer_id: certHic.insurer_id ?? dbHic.insurer_id,
      insurer_number: certHic.insurer_number ?? dbHic.insurer_number,
      insurer_name: certHic.insurer_name ?? dbHic.insurer_name,
      insurer_kind_name: certHic.insurer_kind_name ?? dbHic.insurer_kind_name,
      insurance_type_id: certHic.insurance_type_id ?? dbHic.insurance_type_id,
      relationship_id: certHic.relationship_id ?? dbHic.relationship_id,
      card_mark: certHic.card_mark ?? dbHic.card_mark,
      card_number: certHic.card_number ?? dbHic.card_number,
      qualification_date: certHic.qualification_date ?? dbHic.qualification_date,
      expiration_date: certHic.expiration_date ?? dbHic.expiration_date,
      patient_cost_rate: certHic.patient_cost_rate ?? dbHic.patient_cost_rate,
      cost_rate: certHic.cost_rate ?? dbHic.cost_rate,
      family_cost_rate: certHic.family_cost_rate ?? dbHic.family_cost_rate,
      name: certHic.name ?? dbHic.name,
      name_kana: certHic.name_kana ?? dbHic.name_kana,
    };
    mergedData = {
      ...mergedData,
      health_insurance_card: mergedHic,
      health_insurance_cards: mergedData.health_insurance_cards?.map(c =>
        c.is_current ? mergedHic : c
      ),
    };
  }

  return mergedData;
};

/**
 * RA2/A2 copyInit: new registration pre-filled from source patient.
 * Clears patient / section DB ids and serial; clears only 受診者情報 氏名・カナ・生年月日 (not 保険証被保険者).
 */
export const patientFormDataForNewRegistrationCopy = (
  normalized: PatientFormData
): PatientFormData => {
  const base = normalizePatientData(JSON.parse(JSON.stringify(normalized)) as PatientFormData);

  const clearPatientInfoIdentity = (pi: PatientInformation): PatientInformation => ({
    ...pi,
    id: undefined,
    patient_id: undefined,
    patient_information_id: null,
    name: '',
    name_kana: '',
    date_of_birth: '',
    age: null,
    sex_id: null,
    is_current: true,
  });

  const clearHealthCardIds = (c: HealthInsuranceCard): HealthInsuranceCard => ({
    ...c,
    id: undefined,
    patient_id: undefined,
    health_insurance_card_id: null,
    is_current: true,
  });

  const clearMedicalAidIds = (a: MedicalAid): MedicalAid => ({
    ...a,
    id: undefined,
    patient_id: undefined,
    medical_aid_id: null,
    is_current: true,
  });

  const sourcePi =
    base.current_patient_information ||
    base.patient_informations?.find(i => i.is_current) ||
    base.patient_informations?.[0];
  const clearedPi = sourcePi
    ? clearPatientInfoIdentity({ ...sourcePi })
    : clearPatientInfoIdentity({ ...defaultPatientFormData.current_patient_information });

  const sourceCard =
    base.health_insurance_cards?.find(c => c.is_current) || base.health_insurance_cards?.[0];
  const sourceAid = base.medical_aids?.find(a => a.is_current) || base.medical_aids?.[0];

  return normalizePatientData({
    ...base,
    id: 0,
    serial_number: 0,
    official: false,
    patient: base.patient
      ? { ...base.patient, patient_id: null, serial_number: '', official: false }
      : { ...defaultPatientData },
    current_patient_information: clearedPi,
    patient_informations: clearedPi ? [clearedPi] : [],
    health_insurance_cards: sourceCard ? [clearHealthCardIds({ ...sourceCard })] : [],
    medical_aids: sourceAid ? [clearMedicalAidIds({ ...sourceAid })] : [],
  });
};

/**
 * Convert PatientFormData to PatientFormSchemaType for React Hook Form.
 */
export const toFormValues = (data: PatientFormData): PatientFormSchemaType => {
  const patientInfo = data.current_patient_information || data.patient_informations?.[0];
  const healthInsurance =
    data.health_insurance_cards?.find(card => card.is_current) || data.health_insurance_cards?.[0];
  const medicalAid = data.medical_aids?.find(aid => aid.is_current) || data.medical_aids?.[0];
  const insuranceTypeId = healthInsurance?.insurance_type_id ?? 1;
  const shouldClearElderlyInsuranceCertificate = insuranceTypeId === 2 || insuranceTypeId === 3;
  const normalizedMedicalAidInsurerTypeId =
    medicalAid?.insurer_type_id === 1 ? 0 : (medicalAid?.insurer_type_id ?? 0);
  const isNoMedicalAid = normalizedMedicalAidInsurerTypeId === 0;

  return {
    patient: {},
    patient_information: {
      id: patientInfo?.id,
      name: patientInfo?.name || '',
      name_kana: patientInfo?.name_kana || '',
      sex_id: (patientInfo?.sex_id as 1 | 2) || undefined,
      date_of_birth: normalizeDateField(patientInfo?.date_of_birth),
      postal_code1: normalizePostalCode(patientInfo?.postal_code1 || ''),
      postal_code2: normalizePostalCode(patientInfo?.postal_code2 || ''),
      address1: normalizeAddress(patientInfo?.address1 || ''),
      address2: normalizeAddress(patientInfo?.address2 || ''),
      address3: normalizeAddress(patientInfo?.address3 || ''),
      address_name: patientInfo?.address_name || '',
      address_kana: patientInfo?.address_kana || '',
      telephone: normalizePostalCode(patientInfo?.telephone || ''),
      summary: patientInfo?.summary || '',
      remarks: patientInfo?.remarks || '',
      is_exclusion: patientInfo?.is_exclusion ?? false,
      is_non_output_telephone: patientInfo?.is_non_output_telephone ?? false,
      gaiji_check: patientInfo?.gaiji_check ?? false,
      address2_hidden: patientInfo?.address2_hidden ?? false,
      is_facility_resident: patientInfo?.is_facility_resident ?? false,
      address3_check: patientInfo?.address3_check ?? false,
      is_special_area_addition: patientInfo?.is_special_area_addition ?? false,
      available_from: patientInfo?.available_from
        ? String(patientInfo.available_from).slice(0, 10)
        : '',
      available_to: patientInfo?.available_to ? String(patientInfo.available_to).slice(0, 10) : '',
    },
    health_insurance_card: {
      id: healthInsurance?.id,
      relationship_id: healthInsurance?.relationship_id ?? null,
      insurance_type_id: insuranceTypeId,
      insurer_id: healthInsurance?.insurer_id,
      insurer_number: normalizeInsurerNumberField(String(healthInsurance?.insurer_number ?? '')),
      insurer_name: healthInsurance?.insurer_name || '',
      insurer_kind_name: healthInsurance?.insurer_kind_name || '',
      // `?? ''` (not `||`): patient_cost_rate = 0 is a legitimate saved value
      // (生活保護 full coverage / なし); `|| ''` would wipe it on load.
      patient_cost_rate: String(healthInsurance?.patient_cost_rate ?? ''),
      // Match HealthInsuranceSection/useHealthInsuranceNormalization mount-time auto-fill. Some
      // patients have no current insurance card, so these are absent from the API but appear in RHF.
      patient_cost_id: healthInsurance?.patient_cost_id ?? 0,
      elderly_insurance_certificate_id: shouldClearElderlyInsuranceCertificate
        ? 0
        : (healthInsurance?.elderly_insurance_certificate_id ?? 1),
      // 記号・番号 blur normalises half-width → full-width (see handleCardMarkBlur /
      // handleCardNumberBlur). Preserve the stored value on reload so it isn't flipped
      // back to half-width after 登録.
      card_mark: healthInsurance?.card_mark || '',
      card_number: healthInsurance?.card_number || '',
      qualification_date: normalizeDateField(healthInsurance?.qualification_date),
      expiration_date: normalizeDateField(healthInsurance?.expiration_date),
      name: healthInsurance?.name || '',
      name_kana: healthInsurance?.name_kana || '',
      sex_id: healthInsurance?.sex_id ?? undefined,
      date_of_birth: normalizeDateField(healthInsurance?.date_of_birth),
      postal_code1: normalizePostalCode(healthInsurance?.postal_code1 || ''),
      postal_code2: normalizePostalCode(healthInsurance?.postal_code2 || ''),
      address1: normalizeAddress(healthInsurance?.address1 || ''),
      address2: normalizeAddress(healthInsurance?.address2 || ''),
      address_kana: healthInsurance?.address_kana || '',
      telephone: normalizePostalCode(healthInsurance?.telephone || ''),
      claim_type_id:
        healthInsurance?.claim_type_id ?? (insuranceTypeId === 3 ? CLAIM_TYPE_NONE_ID : null),
      cost_rate: toOptionalNumber(healthInsurance?.cost_rate),
      family_cost_rate: toOptionalNumber(healthInsurance?.family_cost_rate),
    },
    medical_aid: {
      id: medicalAid?.id,
      insurer_type_id: normalizedMedicalAidInsurerTypeId,
      insurer_id: medicalAid?.insurer_id,
      medical_aid_number: normalizeHalfWidthCodeField(medicalAid?.medical_aid_number || ''),
      medical_aid_patient_number: normalizeHalfWidthCodeField(
        medicalAid?.medical_aid_patient_number || ''
      ),
      // `?? ''` (not `||`): insurer_cost_rate = 0 is a legitimate saved value (e.g. 生活保護
      // full coverage); `|| ''` would coerce it to empty on load and wipe the DB value.
      insurer_cost_rate: String(medicalAid?.insurer_cost_rate ?? ''),
      // A2 seeds both hidden #cost_rate / #family_cost_rate to the saved insurer_cost_rate
      // on load; real distinct values arrive when the user picks an insurer via the modal.
      cost_rate: String(medicalAid?.insurer_cost_rate ?? ''),
      family_cost_rate: String(medicalAid?.insurer_cost_rate ?? ''),
      is_partial_aid: medicalAid?.is_partial_aid ?? false,
      is_special: medicalAid?.is_special ?? false,
      calc_type: isNoMedicalAid ? null : (medicalAid?.calc_type ?? null),
      limit_amount: isNoMedicalAid ? null : (medicalAid?.limit_amount ?? null),
      limit_num_month: isNoMedicalAid ? null : (medicalAid?.limit_num_month ?? null),
      limit_amount_month: isNoMedicalAid ? null : (medicalAid?.limit_amount_month ?? null),
      claim_type_id: isNoMedicalAid ? null : (medicalAid?.claim_type_id ?? null),
      application_start_date: normalizeDateField(medicalAid?.application_start_date),
      application_end_date: normalizeDateField(medicalAid?.application_end_date),
      is_end_medical_aid: medicalAid?.is_end_medical_aid ?? false,
    },
    _bulkHistoryAvailableToMinus: undefined,
    _bulkHistoryQualificationDate: undefined,
  };
};

/**
 * Get default form values.
 */
export const getDefaultFormValues = (): PatientFormSchemaType => ({
  patient: {},
  patient_information: {
    name: '',
    name_kana: '',
    sex_id: null as any,
    date_of_birth: '',
    postal_code1: '',
    postal_code2: '',
    address1: '',
    address2: '',
    address3: '',
    address_name: '',
    address_kana: '',
    telephone: '',
    summary: '',
    remarks: '',
    is_exclusion: false,
    is_non_output_telephone: false,
    gaiji_check: false,
    address2_hidden: false,
    is_facility_resident: false,
    address3_check: false,
    is_special_area_addition: false,
    available_from: '',
    available_to: '',
  },
  health_insurance_card: {
    id: undefined,
    relationship_id: null,
    insurance_type_id: 1,
    insurer_id: undefined,
    insurer_number: '',
    insurer_name: '',
    insurer_kind_name: '',
    patient_cost_rate: '',
    // Match HealthInsuranceSection's mount-time "なし" auto-fill so the form-change
    // guard doesn't see a phantom diff when the user clicks 入力クリア on an untouched form.
    patient_cost_id: 0,
    elderly_insurance_certificate_id: 1,
    card_mark: '',
    card_number: '',
    qualification_date: '',
    expiration_date: '',
    name: '',
    name_kana: '',
    sex_id: undefined,
    date_of_birth: '',
    postal_code1: '',
    postal_code2: '',
    address1: '',
    address2: '',
    address_kana: '',
    telephone: '',
    claim_type_id: undefined,
    cost_rate: undefined,
    family_cost_rate: undefined,
  },
  medical_aid: {
    id: undefined,
    insurer_type_id: 0,
    insurer_id: undefined,
    medical_aid_number: '',
    medical_aid_patient_number: '',
    insurer_cost_rate: '',
    cost_rate: '',
    family_cost_rate: '',
    is_partial_aid: false,
    is_special: false,
    calc_type: undefined,
    limit_amount: undefined,
    limit_num_month: undefined,
    limit_amount_month: undefined,
    claim_type_id: undefined,
    application_start_date: '',
    application_end_date: '',
    is_end_medical_aid: false,
  },
  _bulkHistoryAvailableToMinus: undefined,
  _bulkHistoryQualificationDate: undefined,
});
